import React from "react";
import {Box, IconButton, Menu, MenuButton, MenuItem, MenuList, Portal, Text} from "@chakra-ui/react";
import {CloseIcon} from "@chakra-ui/icons";
import {QC_PROBLEM_TAGS} from "../../models/constants/qcProblemTags.ts";
import type {QCProblemTag} from "../../models/constants/qcProblemTags.ts";

interface QCProblemTagSelectProps {
  readonly isClearable?: boolean;
  readonly isDisabled?: boolean;
  readonly selectedTag: QCProblemTag | null;
  readonly onSelectTag: (tag: QCProblemTag) => void;
  readonly onClearTag: () => void;
}

const QCProblemTagSelect: React.FC<QCProblemTagSelectProps> = ({isClearable = true, isDisabled = false, selectedTag, onSelectTag, onClearTag}) => (
  <Box display="flex" alignItems="center" gap={1}>
    <Menu matchWidth isLazy strategy="fixed">
      <MenuButton
        disabled={isDisabled}
        w="full"
        minH="40px"
        px={2}
        fontSize="xs"
        display="flex"
        alignItems="center"
        bg="gray.700"
        border="1px solid"
        borderColor="gray.600"
        rounded="md"
        cursor={isDisabled ? "not-allowed" : "pointer"}
        opacity={isDisabled ? 0.6 : 1}
        _hover={isDisabled ? undefined : {borderColor: "brandPurple.400"}}
        _expanded={{borderColor: "brandPurple.500"}}
        _active={{transform: "scale(0.96)"}}
        transitionProperty="border-color, transform"
        transitionDuration="0.2s"
      >
        <Text color={selectedTag ? "white" : "gray.400"} fontSize="xs" textAlign="left" noOfLines={1}>
          {selectedTag ?? "Problem tag"}
        </Text>
      </MenuButton>
      <Portal>
        <MenuList zIndex="popover" maxH="min(320px, 45dvh)" overflowY="auto" overscrollBehavior="contain" bg="gray.800" border="1px solid" borderColor="gray.700" py={2} px={2} minW="unset" boxShadow="lg" textAlign="left">
          {QC_PROBLEM_TAGS.map((tag) => (
            <MenuItem key={tag} bg="transparent" color="white" _hover={{bg: "gray.700"}} _focus={{bg: "gray.700"}} justifyContent="flex-start" py={1} minH="40px" onClick={() => onSelectTag(tag)}>
              <Box border="1px solid" borderColor="gray.500" color="white" bg="transparent" px={2} py={0.5} rounded="md" fontSize="xs" fontWeight="medium" whiteSpace="nowrap" textAlign="left">
                {tag}
              </Box>
            </MenuItem>
          ))}
        </MenuList>
      </Portal>
    </Menu>
    {selectedTag && (
      <IconButton aria-label="Clear problem tag" icon={<CloseIcon />} size="xs" variant="ghost" color="gray.300" minW="40px" minH="40px" _active={{transform: "scale(0.96)"}} transitionProperty="background-color, color, transform" transitionDuration="0.2s" onClick={onClearTag} isDisabled={isDisabled || !isClearable} />
    )}
  </Box>
);

export default QCProblemTagSelect;
